/**
 * FranchisesSection — Horizontal scrollable card list for franchises,
 * studios and streaming platforms. Three tabs in one section.
 * Shown on /films just below WhereToWatch.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Clapperboard, Building2, Tv, ExternalLink, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// First-letter fallback shown when the logo URL fails to load.
const TYPE_GRAD = {
  franchise: 'from-pink-500/30 via-purple-500/30 to-indigo-500/30',
  studio: 'from-amber-400/30 via-orange-500/30 to-red-500/30',
  streaming: 'from-cyan-400/30 via-blue-500/30 to-indigo-600/30',
};
const FranchiseLogo = ({ image, title, type }) => {
  const [failed, setFailed] = useState(false);
  if (!image || failed) {
    return (
      <div className={`aspect-square w-full flex items-center justify-center bg-gradient-to-br ${TYPE_GRAD[type] || 'from-white/10 to-white/5'}`}>
        <span className="text-5xl font-black text-white/90 drop-shadow-lg">{(title || '?').trim()[0]?.toUpperCase()}</span>
      </div>
    );
  }
  return (
    <div className="aspect-square bg-zinc-900 flex items-center justify-center p-4 overflow-hidden">
      <img
        src={image}
        alt={title}
        className="max-w-full max-h-full object-contain"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

// v50.10.8e — only Streaming is shown to users (franchises/studios kept in admin + API)
const TYPES = [
  { id: 'streaming', icon: Tv, accent: 'from-cyan-300 to-blue-500' },
];

const FranchisesSection = () => {
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('streaming');

  const load = useCallback(async (type) => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/franchises?type=${type}&lang=${language}`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { load(activeType); }, [activeType, load]);

  // Auto-refresh when an admin creates/updates/deletes an FSS entry elsewhere in the app.
  useEffect(() => {
    const handler = () => load(activeType);
    window.addEventListener('franchises:changed', handler);
    return () => window.removeEventListener('franchises:changed', handler);
  }, [activeType, load]);

  return (
    <section data-testid="franchises-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Tv className="w-6 h-6 text-cyan-300" />
          {t('franchises.type_streaming') || 'Streaming'}
        </h2>
      </div>

      {/* Type tabs (hidden when a single type) */}
      <div className={`flex gap-2 mb-4 overflow-x-auto scrollbar-none ${TYPES.length < 2 ? 'hidden' : ''}`}>
        {TYPES.map((tp) => {
          const Icon = tp.icon;
          const isActive = activeType === tp.id;
          return (
            <button
              key={tp.id}
              type="button"
              onClick={() => setActiveType(tp.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                isActive
                  ? `bg-gradient-to-r ${tp.accent} text-white shadow-lg`
                  : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
              data-testid={`franchises-tab-${tp.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(`franchises.type_${tp.id}`) || tp.id}
            </button>
          );
        })}
      </div>

      {/* Cards */}
      {loading ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-40 h-40 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-white/40 text-sm" data-testid="franchises-empty">
          {t('franchises.empty') || 'Aucun élément'}
        </div>
      ) : (
        <div className="overflow-x-auto pb-2 -mx-4 px-4">
          <div className="flex gap-3" style={{ minWidth: 'max-content' }} data-testid="franchises-list">
            {items.map((it) => (
              <div
                key={it.id}
                className="flex-shrink-0 w-40 sm:w-48 rounded-2xl overflow-hidden bg-black border border-white/10 hover:border-purple-400/40 transition-all"
                data-testid={`franchise-${it.id}`}
              >
                <FranchiseLogo image={it.image_url} title={it.title} type={it.type} />
                <div className="p-3">
                  <h3 className="text-sm font-bold text-white truncate" title={it.title}>{it.title}</h3>
                  {it.description && (
                    <p className="text-[11px] text-white/60 mt-1 line-clamp-2" title={it.description}>{it.description}</p>
                  )}
                  {it.price_label && (
                    <p className="text-[11px] text-emerald-300 mt-0.5 font-semibold">{it.price_label}</p>
                  )}
                  {it.is_free && !it.price_label && (
                    <p className="text-[11px] text-emerald-300 mt-0.5 font-semibold">{t('franchises.free') || 'Gratuit'}</p>
                  )}
                  {it.external_url && (
                    <a
                      href={it.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-purple-300 hover:text-purple-200"
                      data-testid={`franchise-link-${it.id}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {it.type === 'studio' ? (t('franchises.wikipedia') || 'Wikipédia') : (t('franchises.visit') || 'Visiter')}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default FranchisesSection;
